/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
/* eslint object-curly-newline: ["error", { "multiline": true }] */
/* eslint no-param-reassign: ["error", { "props": false }] */
import lodash from 'lodash';
import axios from 'axios';
import qs from 'qs';
import states from '../../helpers/states';

// initial state
const state = {
  locations: [],
  orders: [],
  states,
  products: null,
  batteryLine: null,
  batterySize: null,
  packSize: null,
  quantity: null,
  imprint: null,
  orderProducts: [],
  productTemplate: {
    batteryLine: null,
    batterySize: null,
    packSize: null,
    quantity: null,
    imprint: null,
  },
  currentSection: 'addItems',
  selectedShippingLocationID: null,
  paymentType: 'Credit Card',
  paymentInfo: {
    cardholderName: null,
    cardNumber: null,
    expDate: null,
    cvv: null,
  },
  shippingInfo: {
    company: null,
    first_name: null,
    last_name: null,
    email: null,
    phone: null,
    street: null,
    street_2: null,
    city: null,
    state: null,
    zip: null,
  },
  billingInfo: {
    street: null,
    city: null,
    state: null,
    zip: null,
  },
  loaders: {
    products: false,
    locations: false,
    submitting: false,
  },
};

// getters
const getters = {
  locations: (state) => state.locations,
  orders: (state) => state.orders,
  products: (state) => state.products,
  orderProducts: (state) => state.orderProducts,
  loaders: (state) => state.loaders,
  states: (state) => lodash.map(state.states, (state) => ({ label: state.abbrev, value: state.abbrev })),
  batteryLine: (state) => state.batteryLine,
  batterySize: (state) => state.batterySize,
  packSize: (state) => state.packSize,
  quantity: (state) => state.quantity,
  imprint: (state) => state.imprint,
  currentSection: (state) => state.currentSection,
  shippingLocation: (state) => state.shippingLocation,
  selectedShippingLocationID: (state) => state.selectedShippingLocationID,
  shippingInfo: (state) => state.shippingInfo,
  billingInfo: (state) => state.billingInfo,
  paymentType: (state) => state.paymentType,
  paymentInfo: (state) => state.paymentInfo,
  paymentOptions: (state, getters, rootState) => rootState.main.member.battery_store_payment_options,
  member_id: (state, getters, rootState) => rootState.main.member_id,
  user: (state, getters, rootState) => rootState.auth.user,
  batteryLines: (state) => {
    return lodash.chain(state.products).uniqBy('battery_line').value();
  },
  batterySizes: (state, getters) => {
    return getters.batteryLine !== undefined
      ? lodash.chain(state.products)
        .filter((batteryLine) => batteryLine.battery_line === getters.batteryLine)
        .map('size')
        .uniq()
        .value()
      : [];
  },
  packSizes: (state, getters) => {
    return getters.batterySize !== undefined
      ? lodash.chain(state.products)
        .filter(batteryLine => batteryLine.battery_line === getters.batteryLine)
        .filter(batterySize => batterySize.size === getters.batterySize)
        .map('pack_option').uniq().value()
      : [];
  },
  product: (state, getters) => {
    return getters.packSize !== undefined
      ? lodash.chain(state.products)
        .filter(batteryLine => batteryLine.battery_line === getters.batteryLine)
        .filter(batterySize => batterySize.size === getters.batterySize)
        .find(packSize => packSize.pack_option === getters.packSize)
        .value()
      : null;
  },
  description: (state, getters) => {
    return getters.product !== undefined ? getters.product.description : '';
  },
  productFormItem: (state, getters) => {
    return {
      ...getters.product,
      quantity: getters.quantity,
      imprint: getters.imprint,
      price: getters.imprint === 'No Imprint' ? getters.product.base_price : getters.product.price_imprint,
      subtotal: getters.quantity * parseInt(getters.imprint === 'No Imprint' ? getters.product.base_price : getters.product.price_imprint, 10),
      sku: getters.imprint !== 'No Imprint' && getters.product.sku_imprint !== '' ? getters.product.sku_imprint : getters.product.sku,
      price_per_cell: getters.imprint === 'No Imprint' ? getters.product.price_per_cell_base : getters.product.price_per_cell_imprint,
    };
  },
  orderTotal: (state, getters) => lodash.reduce(getters.orderProducts, (sum, product) => {
    if (product.imprint === 'No Imprint') {
      return sum + parseInt(product.quantity, 10) * parseInt(product.base_price, 10);
    }
    return sum + parseInt(product.quantity, 10) * parseInt(product.price_imprint, 10);
  }, 0),
  totalBatteries: (state, getters) => lodash.reduce(getters.orderProducts, (sum, product) => {
    return sum + parseInt(product.quantity, 10) * parseInt(product.total_batteries, 10);
  }, 0),
  selectedShippingLocation: (state) => lodash.find(
    state.locations,
    (location) => location.id === state.selectedShippingLocationID,
  ),
  selectedShippingLocationInfo: (state, getters) => {
    return {
      street: getters.selectedShippingLocation !== undefined ? getters.selectedShippingLocation.street : null,
      street_2: getters.selectedShippingLocation !== undefined ? getters.selectedShippingLocation.street_2 : null,
      city: getters.selectedShippingLocation !== undefined ? getters.selectedShippingLocation.city : null,
      state: getters.selectedShippingLocation !== undefined ? getters.selectedShippingLocation.state : null,
      zip: getters.selectedShippingLocation !== undefined ? getters.selectedShippingLocation.zip : null,
      phone: getters.selectedShippingLocation !== undefined ? getters.selectedShippingLocation.phone : null,
    };
  },
  shippingInfoValid: (state) => {
    return state.shippingInfo.first_name
      && state.shippingInfo.last_name
      && state.shippingInfo.company
      && state.shippingInfo.email
      && state.shippingInfo.phone
      && state.shippingInfo.street
      && state.shippingInfo.city
      && state.shippingInfo.state
      && state.shippingInfo.zip;
  },
  orderCommonPayload: (state, getters) => {
    return {
      shippingInfo: getters.shippingInfo,
      orderProducts: getters.orderProducts,
      orderTotal: getters.orderTotal,
      paymentType: getters.paymentType,
      user_email: getters.user.userInfo.email,
      member_id: getters.member_id,
      user_id: getters.user.userInfo.id,
    };
  },
  orderPayload: (state, getters) => {
    if (getters.paymentType === 'Credit Card') {
      return {
        ...getters.orderCommonPayload,
        paymentInfo: getters.paymentInfo,
        billingInfo: getters.billingInfo,
      };
    }
    return getters.orderCommonPayload;
  },
};

// actions
const actions = {
  addToOrder({ commit, getters }) {
    commit('ADD_TO_ORDER', getters.productFormItem);
    commit('RESET_PRODUCT_FORM');
  },
  removeItem({ commit }, key) {
    commit('REMOVE_ITEM', key);
  },
  setProperty({ commit }, payload) {
    commit('SET_PROPERTY', payload);
  },
  setObjectProperty({ commit }, payload) {
    commit('SET_OBJECT_PROPERTY', payload);
  },
  getProducts({ commit }) {
    commit('TOGGLE_LOADING', { key: 'products', value: true });
    return axios.get(`${CONFIG.get('BASE_URL')}/api/battery-store/getProducts/`)
      .then((response) => {
        commit('SET_PROPERTY', { key: 'products', value: response.data });
        commit('TOGGLE_LOADING', { key: 'products', value: false });
      }).catch((err) => {
        this.handleErrorResponse(err);
      });
  },

  getOrders({ commit }) {
    commit('TOGGLE_LOADING', { key: 'products', value: true });
    return axios.get(`${CONFIG.get('BASE_URL')}/api/battery-store/getOrders/`)
      .then((response) => {
        commit('SET_PROPERTY', { key: 'orders', value: response.data });
        commit('TOGGLE_LOADING', { key: 'orders', value: false });
      }).catch((err) => {
        this.handleErrorResponse(err);
      });
  },

  getLocations({ commit, state, getters }) {
    commit('TOGGLE_LOADING', { key: 'locations', value: true });
    return axios.get(`/api/locations/getLocations/`)
      .then((response) => {
        commit('SET_PROPERTY', { key: 'locations', value: response.data });
        commit('TOGGLE_LOADING', { key: 'locations', value: false });
      }).catch((thrown) => {});
  },

  setLocationInfo({ commit, state, getters }) {
    commit('SET_PROPERTY', {
      key: 'shippingInfo',
      value: {
        ...state.shippingInfo,
        ...getters.selectedShippingLocationInfo,
      },
    });
  },

  touchLocationInfo({ commit }) {
    commit('SET_PROPERTY', {
      key: 'shippingInfo',
      value: state.shippingInfo,
    });
  },

  submitOrder({ commit, getters, dispatch }) {
    commit('TOGGLE_LOADING', { key: 'submitting', value: true });
    axios.post(`${CONFIG.get('BASE_URL')}/api/battery-store/submitOrder/`, qs.stringify(getters.orderPayload))
      .then((response) => {
        commit('TOGGLE_LOADING', { key: 'submitting', value: false });
        commit('SET_OBJECT_PROPERTY', { object: 'paymentInfo', key: 'cardNumber', value: null });
        commit('SET_OBJECT_PROPERTY', { object: 'paymentInfo', key: 'expDate', value: null });
        commit('SET_OBJECT_PROPERTY', { object: 'paymentInfo', key: 'cvv', value: null });
        if (response.data === 'Transaction Successful') {
          dispatch('flash/addMessage', { text: 'Order Received!' }, { root: true });
          commit('SET_PROPERTY', { key: 'currentSection', value: 'successMessage' });
        } else {
          dispatch('flash/addMessage', { text: 'Transaction Error', type: 'error' }, { root: true });
          commit('SET_PROPERTY', { key: 'currentSection', value: 'errorMessage' });
        }
      })
      .catch((error) => {
        commit('TOGGLE_LOADING', { key: 'submitting', value: false });
        console.error(error);
      });
  },

  resetForm({ commit }) {
    commit('SET_PROPERTY', { key: 'orderProducts', value: [] });
    commit('SET_PROPERTY', { key: 'currentSection', value: 'addItems' });
  },
};

// mutations
const mutations = {
  SET_PROPERTY(state, payload) {
    state[payload.key] = payload.value;
  },
  SET_OBJECT_PROPERTY(state, payload) {
    state[payload.object][payload.key] = payload.value;
  },
  TOGGLE_LOADING(state, payload) {
    state.loaders[payload.key] = payload.value;
  },
  ADD_TO_ORDER(state, payload) {
    state.orderProducts.push(payload);
  },
  REMOVE_ITEM(state, key) {
    state.orderProducts.splice(key, 1);
  },
  RESET_PRODUCT_FORM(state) {
    state.batteryLine = null;
    state.batterySize = null;
    state.packSize = null;
    state.quantity = null;
    state.imprint = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
